/**
 * Product (PDP) Page Widget Registry.
 *
 * Pure WidgetType -> widget mapping for the PDP PL pipeline. Reuses the same
 * shared widgets as CLP for cross-page widgets (breadcrumbs, FAQ, recommendations).
 * PDP-specific widgets (gallery, info, options, etc.) will be added in follow-up tickets.
 */

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'

import { asBaseWidget, PageWidgetRegistry } from '@src/components/pl-base-widget'
import { AccordionWidget } from '@src/components/widgets/accordion'
import { BrandComparisonWidget } from '@src/components/widgets/brand-comparison'
import { BreadcrumbsPdpWidget } from '@src/components/widgets/breadcrumbs-pdp'
import { BundleGalleryV2Widget } from '@src/components/widgets/bundle-gallery-v2'
import { BundlePurchaseWidget } from '@src/components/widgets/bundle-purchase'
import { BundlePurchaseV2Widget } from '@src/components/widgets/bundle-purchase-v2'
import { DisclaimerWidget } from '@src/components/widgets/disclaimer'
import { FAQWidget } from '@src/components/widgets/faq'
import { FaqItemWidget } from '@src/components/widgets/faq-item'
import { FeatureCardsWidget } from '@src/components/widgets/feature-cards'
import { FeatureShowcaseWidget } from '@src/components/widgets/feature-showcase'
import { HtmlWidget } from '@src/components/widgets/html'
import { InfluencerCardWidget } from '@src/components/widgets/influencer-card'
import { InfluencerSectionWidget } from '@src/components/widgets/influencer-section'
import { MediaTextMosaicWidget } from '@src/components/widgets/media-text-mosaic'
import { ProductComparisonCarouselWidget } from '@src/components/widgets/product-comparison-carousel'
import { ProductGalleryWidget } from '@src/components/widgets/product-gallery'
import { ProductGalleryV2Widget } from '@src/components/widgets/product-gallery-v2'
import { ProductGridPdpWidget } from '@src/components/widgets/product-grid/product-grid-pdp-widget'
import { ProductGridCarouselPdpWidget } from '@src/components/widgets/product-grid-carousel/product-grid-carousel-pdp-widget'
import { ProductInfoWidget } from '@src/components/widgets/product-info'
import { ProductOverviewWidget } from '@src/components/widgets/product-overview'
import { ProductPurchaseWidget } from '@src/components/widgets/product-purchase'
import { ProductPurchaseV2 } from '@src/components/widgets/product-purchase-v2'
import { ProductRecommendationsPdpWidget } from '@src/components/widgets/product-reco/product-reco-pdp-widget'
import { ProductSummaryWidget } from '@src/components/widgets/product-summary'
import { ReviewsAllButtonWidget } from '@src/components/widgets/reviews/reviews-all-button'
import { ReviewsFullWidget } from '@src/components/widgets/reviews/reviews-full'
import { ReviewsGalleryWidget } from '@src/components/widgets/reviews/reviews-gallery'
import { ReviewsHighlightWidget } from '@src/components/widgets/reviews/reviews-highlight'
import { ReviewsListWidget } from '@src/components/widgets/reviews/reviews-list'
import { ReviewsMiniWidget } from '@src/components/widgets/reviews/reviews-mini'
import { ReviewsNoContentWidget } from '@src/components/widgets/reviews/reviews-no-content'
import { ReviewsSummaryWidget } from '@src/components/widgets/reviews/reviews-summary'
import { SingleProductCardWidget } from '@src/components/widgets/single-product-card'
import { TransparentPricingWidget } from '@src/components/widgets/transparent-pricing'
import { TrustBadgesWidget } from '@src/components/widgets/trust-badges'

export function getWidgetRegistry(): PageWidgetRegistry {
  return {
    // PDP above-the-fold containers (v2 nested): PRODUCT_OVERVIEW wraps PRODUCT_GALLERY + PRODUCT_INFO;
    // PRODUCT_INFO wraps PRODUCT_PURCHASE + PRODUCT_SUMMARY (+ accordion/brand-comparison later). Both
    // carry no data — they arrange their pre-rendered children via renderedChildren. Both MUST be
    // registered: the renderer only descends into resolved (registered) widgets, so an unregistered
    // container would render a placeholder and never reach the nested PRODUCT_PURCHASE.
    [WidgetType.PRODUCT_OVERVIEW]: asBaseWidget(ProductOverviewWidget),
    [WidgetType.PRODUCT_INFO]: asBaseWidget(ProductInfoWidget),
    // PDP buy box (PRODUCT_PURCHASE): adapts proto -> Product and reuses the legacy
    // ProductFormWrapper for parity with the non-PL flow. Built component-by-component.
    [WidgetType.PRODUCT_PURCHASE]: asBaseWidget(ProductPurchaseWidget),
    // Bundle buy box — the BE emits it instead of PRODUCT_PURCHASE on a bundle; reuses the legacy
    // BundleProductPurchase via the proto -> Bundle adapter.
    [WidgetType.BUNDLE_PURCHASE]: asBaseWidget(BundlePurchaseWidget),
    // SVD bundle buy box (BUNDLE_PURCHASE_V2): the BE emits it instead of BUNDLE_PURCHASE on an
    // enrichVersion=V2 bundle. Adapts the id-slot + productDatas payload to the `Bundle` domain (each
    // constituent through the single-product V2 chain) and renders the shared BundleProductPurchase
    // organism on its SVD branch, so DS and legacy both follow.
    [WidgetType.BUNDLE_PURCHASE_V2]: asBaseWidget(BundlePurchaseV2Widget),
    // SVD bundle gallery (BUNDLE_GALLERY_V2): bundle-level assets scoped by each asset's inline `vdas`
    // map against the published child selection. Replaces PRODUCT_GALLERY on a V2 bundle.
    [WidgetType.BUNDLE_GALLERY_V2]: asBaseWidget(BundleGalleryV2Widget),
    // SVD buy box (PRODUCT_PURCHASE_V2): VDA-native payload adapted to the domain via
    // productPurchaseV2ToSvdWire → svdToProduct. Emitted only on enrichVersion=V2 sessions.
    [WidgetType.PRODUCT_PURCHASE_V2]: asBaseWidget(ProductPurchaseV2),
    // PDP product description (nested under PRODUCT_INFO): summary HTML + value-prop icon chips.
    // Reuses the legacy ProductDescription atom (summary + ProductDetailsIconSection) for parity.
    [WidgetType.PRODUCT_SUMMARY]: asBaseWidget(ProductSummaryWidget),
    // PDP brand comparison ("Beyond compare" table). Renders its own competitor brand tables from
    // BrandComparisonWidgetData, but sources the live Quince price + resolved variant + gift-card/
    // servings scalars from the PRODUCT_PURCHASE buy box via the shared page-widget store, so it reacts
    // to variant selection like the non-PL section.
    [WidgetType.BRAND_COMPARISON]: asBaseWidget(BrandComparisonWidget),
    // PDP media gallery (nested under PRODUCT_OVERVIEW). Self-contained: renders images/videos from
    // its own ProductGalleryWidgetData (filtered to the selected color) via the legacy
    // SliderImageWithText, mirroring the non-PL PDP gallery.
    [WidgetType.PRODUCT_GALLERY]: asBaseWidget(ProductGalleryWidget),
    // SVD media gallery (PRODUCT_GALLERY_V2, enrichVersion=V2): renders the RESOLVED variant's media
    // (asset pool + per-variant refs) through the same shared gallery components as PRODUCT_GALLERY,
    // so the gallery events fire with SVD-derived context (events master plan T2.3).
    [WidgetType.PRODUCT_GALLERY_V2]: asBaseWidget(ProductGalleryV2Widget),
    // PDP product-details accordion (nested under PRODUCT_INFO): DETAILS / SIZE & FIT / CARE / … rows.
    // ACCORDION is a pure container — each row is a nested HTML child (title + HTML body); the widget
    // reuses the legacy ProductDetailsTabWithReadMore row read-only for exact parity. HTML is registered
    // as a no-op sentinel so the nested children don't log an "unknown widget" warning.
    [WidgetType.ACCORDION]: asBaseWidget(AccordionWidget),
    [WidgetType.HTML]: asBaseWidget(HtmlWidget),
    // PDP PL uses its own breadcrumbs widget: it applies the PDP_BREADCRUMB trim and routes DS vs
    // legacy itself. CLP uses the shared BreadcrumbsWidget (same DS routing, CLP presentation).
    [WidgetType.BREADCRUMBS]: asBaseWidget(BreadcrumbsPdpWidget),
    [WidgetType.DISCLAIMER]: asBaseWidget(DisclaimerWidget),
    [WidgetType.FAQ]: asBaseWidget(FAQWidget),
    // FAQ items arrive as nested FAQ_ITEM widgets (v2 PDP). Each renders as a self-contained
    // accordion item; the parent FAQWidget arranges them via renderedChildren.
    [WidgetType.FAQ_ITEM]: asBaseWidget(FaqItemWidget),
    // PDP recommendations: the v2 layout nests products as SINGLE_PRODUCT_CARD `widgets` under the
    // horizontal-product-list. The container arranges the pre-rendered cards; each card renders
    // itself. PDP uses its own (self-contained) HPL widget — NOT the CLP ProductRecommendationsWidget,
    // which sources cards from `children` and feeds the CLP carousel.
    [WidgetType.HORIZONTAL_PRODUCT_LIST]: asBaseWidget(ProductRecommendationsPdpWidget),
    [WidgetType.PRODUCT_GRID_CAROUSEL]: asBaseWidget(ProductGridCarouselPdpWidget),
    [WidgetType.PRODUCT_LIST]: asBaseWidget(ProductGridPdpWidget),
    [WidgetType.SINGLE_PRODUCT_CARD]: asBaseWidget(SingleProductCardWidget),
    // Content/marketing widgets (STRFRNT-15020). Self-contained, render from their own WidgetData
    // (no nested children); each returns null when its items are empty.
    [WidgetType.PRODUCT_COMPARISON_CAROUSEL]: asBaseWidget(ProductComparisonCarouselWidget),
    [WidgetType.MEDIA_TEXT_MOSAIC]: asBaseWidget(MediaTextMosaicWidget),
    [WidgetType.FEATURE_CARDS]: asBaseWidget(FeatureCardsWidget),
    [WidgetType.TRUST_BADGES]: asBaseWidget(TrustBadgesWidget),
    [WidgetType.TRANSPARENT_PRICING]: asBaseWidget(TransparentPricingWidget),
    // FEATURE_SHOWCASE (legacy FEATURE_SECTION_2): image/video + icon/title/subtitle grid via the
    // shared responsive FeatureSectionTwo atom (one widget covers dweb + mweb).
    [WidgetType.FEATURE_SHOWCASE]: asBaseWidget(FeatureShowcaseWidget),
    // PDP REVIEWS_MINI (mobile-only "Review Highlights"): a container that arranges its three
    // child widgets. REVIEWS_MINI + REVIEWS_ALL_BUTTON carry no data (typed with `never`);
    // REVIEWS_HIGHLIGHT + REVIEWS_LIST render from their own typed data.
    [WidgetType.REVIEWS_MINI]: asBaseWidget(ReviewsMiniWidget),
    [WidgetType.REVIEWS_HIGHLIGHT]: asBaseWidget(ReviewsHighlightWidget),
    [WidgetType.REVIEWS_LIST]: asBaseWidget(ReviewsListWidget),
    [WidgetType.REVIEWS_ALL_BUTTON]: asBaseWidget(ReviewsAllButtonWidget),
    // REVIEWS_FULL (desktop + mobile): container + AI summary. REVIEWS_GALLERY and the
    // full-mode REVIEWS_LIST (filters/sort/pagination + ReviewItem) are the next pieces.
    [WidgetType.REVIEWS_FULL]: asBaseWidget(ReviewsFullWidget),
    [WidgetType.REVIEWS_SUMMARY]: asBaseWidget(ReviewsSummaryWidget),
    [WidgetType.REVIEWS_GALLERY]: asBaseWidget(ReviewsGalleryWidget),
    // REVIEWS_NO_CONTENT: the "No reviews yet" empty state the BE sends in place of the reviews
    // content when a product has none (NoReviews atom + CreateReview popup) — matches non-PL.
    [WidgetType.REVIEWS_NO_CONTENT]: asBaseWidget(ReviewsNoContentWidget),
    // PDP IRL (Quince IRL): the section widget reads its nested INFLUENCER_CARD data directly and
    // routes between the legacy Swiper implementation and the DS block via shouldUseDesignSystem().
    [WidgetType.INFLUENCER_SECTION]: asBaseWidget(InfluencerSectionWidget),
    // INFLUENCER_CARD arrives as nested data consumed by the section above; registered as a
    // no-op widget (renders nothing standalone, suppresses the dev "unknown widget" warning).
    [WidgetType.INFLUENCER_CARD]: asBaseWidget(InfluencerCardWidget),
  }
}
